import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Box,
  Flex,
  Spacer,
  ModalCloseButton,
} from "@chakra-ui/react";
import PrimaryButton from "../../../components/PrimaryButton";
import FormInput from "../../../components/FormInput";

const AirportPickUpModal = ({
  isOpen,
  onClose,
  patientDetails,
  handleAirportPickUpSubmit = () => {},
  isViewOnlyMode = false,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [errors, setErrors] = useState({});
  const [driverDetails, setDriverDetails] = useState({
    driverName: "",
    vehicleNumber: "",
    mobileNumber: "",
    pickupDate: "",
    pickupTime: "",
    vehicleTypeColor: "",
  });

  useEffect(() => {
    if (isOpen && patientDetails?.is_airport_pickup) {
      const latestPickupDetails = patientDetails?.pickup_details?.[0];

      setDriverDetails((prev) => ({
        ...prev,
        driverName: latestPickupDetails?.driver_name || "",
        vehicleNumber: latestPickupDetails?.vehicle_no || "",
        mobileNumber: latestPickupDetails?.mobile_no || "",
        pickupDate: latestPickupDetails?.pickup_date || "",
        pickupTime: latestPickupDetails?.pickup_time || "",
        vehicleTypeColor: latestPickupDetails?.vehicle_type_color || "",
      }));
    } else {
      setCurrentPage(1);
    }
  }, [isOpen, patientDetails]);

  const validateForm = () => {
    const newErrors = {};

    if (!driverDetails.driverName) {
      newErrors.driverName = "Driver Name is required";
    }
    if (!driverDetails.vehicleNumber) {
      newErrors.vehicleNumber = "Vehicle Number is required";
    }
    if (!driverDetails.mobileNumber) {
      newErrors.mobileNumber = "Mobile Number is required";
    } else if (driverDetails.mobileNumber.length !== 10) {
      newErrors.mobileNumber = "Mobile Number must be 10 digits";
    }
    if (!driverDetails.pickupDate) {
      newErrors.pickupDate = "Pickup Date is required";
    }
    if (!driverDetails.pickupTime) {
      newErrors.pickupTime = "Pickup Time is required";
    }
    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < 2)
      setCurrentPage(currentPage + 1);
    if (direction === "prev" && currentPage > 1)
      setCurrentPage(currentPage - 1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDriverDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleAirportPickUpSubmit(driverDetails);
      onClose();
    }
  };

  const handleModalClose = () => {
    setDriverDetails({
      driverName: "",
      vehicleNumber: "",
      mobileNumber: "",
      pickupDate: "",
      pickupTime: "",
      vehicleTypeColor: "",
    });
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="primary.1" textAlign={"center"} fontSize={"2xl"}>
          {currentPage === 1 ? "Airport Pickup" : "Driver Details"}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {currentPage === 1 && (
            <Box>
              <FormInput
                id="patientName"
                label="Patient Name:"
                value={patientDetails?.patient_name || "N/A"}
                isReadOnly
                size="sm"
              />
              <FormInput
                id="airlineDetails"
                label="Airline Details:"
                value={
                  patientDetails?.flight_ticket_details?.airline_details ||
                  "N/A"
                }
                isReadOnly
                size="sm"
              />
              <FormInput
                id="flightNumber"
                label="Flight Number:"
                value={
                  patientDetails?.flight_ticket_details?.flight_number || "N/A"
                }
                isReadOnly
                size="sm"
              />
              <FormInput
                id="arrivalDate"
                label="Arrival Date:"
                value={
                  patientDetails?.flight_ticket_details?.arrival_date || "N/A"
                }
                isReadOnly
                size="sm"
              />
              <FormInput
                id="fromCountry"
                label="From Country:"
                value={
                  patientDetails?.flight_ticket_details?.from_country || "N/A"
                }
                isReadOnly
                size="sm"
              />
              <FormInput
                id="toCountry"
                label="To Country:"
                value={
                  patientDetails?.flight_ticket_details?.to_country || "N/A"
                }
                isReadOnly
                size="sm"
              />
            </Box>
          )}

          {currentPage === 2 && (
            <Box>
              <Flex mb={{ base: 1, md: 2 }}>
                <FormInput
                  id="driverName"
                  label="Driver Name"
                  placeholder="Driver Name"
                  value={driverDetails.driverName}
                  onChange={handleInputChange}
                  isRequired
                  isInvalid={!!errors.driverName}
                  errorMessage={errors.driverName}
                  isReadOnly={isViewOnlyMode}
                />
              </Flex>
              <Flex mb={{ base: 1, md: 2 }}>
                <FormInput
                  id="vehicleNumber"
                  label="Vehicle Number"
                  placeholder="Vehicle Number"
                  value={driverDetails.vehicleNumber}
                  onChange={handleInputChange}
                  isRequired
                  isInvalid={!!errors.vehicleNumber}
                  errorMessage={errors.vehicleNumber}
                  isReadOnly={isViewOnlyMode}
                />
              </Flex>
              <Flex mb={{ base: 1, md: 2 }}>
                <FormInput
                  id="mobileNumber"
                  label="Mobile No"
                  placeholder="Enter Mobile Number"
                  value={driverDetails.mobileNumber}
                  onChange={handleInputChange}
                  isRequired
                  isInvalid={!!errors.mobileNumber}
                  errorMessage={errors.mobileNumber}
                  isReadOnly={isViewOnlyMode}
                  type="tel"
                />
              </Flex>
              <Flex mb={{ base: 1, md: 2 }}>
                <FormInput
                  id="pickupDate"
                  label="Pickup Date"
                  type="date"
                  value={driverDetails.pickupDate}
                  onChange={handleInputChange}
                  isRequired
                  isInvalid={!!errors.pickupDate}
                  errorMessage={errors.pickupDate}
                  name="avoidPastDate"
                  isReadOnly={isViewOnlyMode}
                />
              </Flex>
              <Flex mb={{ base: 1, md: 2 }}>
                <FormInput
                  id="pickupTime"
                  label="Pickup Time"
                  placeholder="Pickup Time"
                  type="time"
                  value={driverDetails.pickupTime}
                  onChange={handleInputChange}
                  isRequired
                  isInvalid={!!errors.pickupTime}
                  errorMessage={errors.pickupTime}
                  isReadOnly={isViewOnlyMode}
                />
              </Flex>
              <Flex mb={{ base: 1, md: 2 }}>
                <FormInput
                  id="vehicleTypeColor"
                  label="Vehicle Type & Color"
                  placeholder="Enter Vehicle Type & Color"
                  value={driverDetails.vehicleTypeColor}
                  onChange={handleInputChange}
                  isReadOnly={isViewOnlyMode}
                />
              </Flex>
            </Box>
          )}
        </ModalBody>
        <ModalFooter>
          <Flex w="100%" alignItems="center">
            {currentPage === 2 && (
              <PrimaryButton onClick={() => handlePageChange("prev")}>
                Previous
              </PrimaryButton>
            )}
            <Spacer />
            {currentPage === 1 && (
              <PrimaryButton onClick={() => handlePageChange("next")}>
                Next
              </PrimaryButton>
            )}
            {currentPage === 2 && !isViewOnlyMode && (
              <PrimaryButton colorScheme="blue" ml={4} onClick={handleSubmit}>
                Submit
              </PrimaryButton>
            )}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AirportPickUpModal;
